import React, { useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { FeedbackApiError, submitFeedback } from './api/feedbackApi';
import { ROUTES } from './routes';

const MIN_LENGTH = 10;
const MAX_LENGTH = 2000;
const IMMEDIATE_REPEAT_MS = 1000;
const SESSION_DUPLICATE_MS = 2 * 60 * 1000;

export function validateFeedbackMessage(message) {
  const normalized = message.trim();
  if (!normalized) return 'Enter a feedback message.';
  if (normalized.length < MIN_LENGTH) return 'Feedback must be at least 10 characters.';
  if (normalized.length > MAX_LENGTH) return 'Feedback must be 2,000 characters or fewer.';
  return '';
}

export function feedbackFailureMessage(error) {
  if (!(error instanceof FeedbackApiError)) return 'We could not send your feedback. Please try again.';
  if (error.status === 400) return 'Check your feedback message and try again.';
  if (error.status === 401) return 'Your sign-in has expired. Sign in again before sending feedback.';
  if (error.status === 409) return 'That feedback was already received recently.';
  if (error.status === 429) return 'You have sent several feedback messages recently. Please try again later.';
  return error.message || 'We could not send your feedback. Please try again.';
}

export default function FeedbackPage() {
  const { authState } = useAuth();
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState({ severity: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const interaction = useRef({
    firstInputAt: null,
    lastInputAt: null,
    hasInputEvents: false,
    lastSubmitAt: null,
    lastAccepted: null,
  });

  const recordInput = () => {
    const at = Date.now();
    interaction.current.firstInputAt ??= at;
    interaction.current.lastInputAt = at;
    interaction.current.hasInputEvents = true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = message.trim();
    const validationError = validateFeedbackMessage(message);
    if (validationError) {
      setStatus({ severity: 'error', message: validationError });
      return;
    }

    const now = Date.now();
    const repeatedImmediateSubmit = interaction.current.lastSubmitAt
      && now - interaction.current.lastSubmitAt < IMMEDIATE_REPEAT_MS;
    const duplicateInSession = interaction.current.lastAccepted
      && interaction.current.lastAccepted.message.toLocaleLowerCase() === trimmed.toLocaleLowerCase()
      && now - interaction.current.lastAccepted.at < SESSION_DUPLICATE_MS;
    interaction.current.lastSubmitAt = now;

    if (honeypot || !interaction.current.hasInputEvents || repeatedImmediateSubmit || duplicateInSession) {
      setStatus({ severity: 'error', message: 'We could not send your feedback. Please wait a moment and try again.' });
      return;
    }
    if (!authState.user) {
      setStatus({ severity: 'error', message: 'Sign in before sending feedback.' });
      return;
    }

    setSubmitting(true);
    setStatus({ severity: '', message: '' });
    try {
      await submitFeedback(trimmed);
      interaction.current.lastAccepted = { message: trimmed, at: Date.now() };
      setMessage('');
      setStatus({ severity: 'success', message: 'Thank you. Your feedback was sent.' });
    } catch (error) {
      setStatus({ severity: 'error', message: feedbackFailureMessage(error) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 } }}>
      <Paper component="section" elevation={2} sx={{ p: { xs: 2, sm: 4 }, textAlign: 'left' }}>
        <Typography component="h1" variant="h4" gutterBottom>Send feedback</Typography>
        <Typography sx={{ mb: 2 }}>
          Tell us what you were trying to do, what happened, and what you expected.
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Do not include passwords, payment details, or sensitive personal information.
        </Alert>

        {!authState.user ? (
          <Alert severity="info">
            Sign in to send feedback.{' '}
            <Button component={RouterLink} to={ROUTES.login} size="small">Sign in</Button>
          </Alert>
        ) : (
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Box
              sx={{
                position: 'absolute',
                width: 1,
                height: 1,
                p: 0,
                m: -1,
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
              aria-hidden="true"
            >
              <label htmlFor="feedback-company">Company</label>
              <input
                id="feedback-company"
                name="company"
                value={honeypot}
                onChange={(event) => setHoneypot(event.target.value)}
                tabIndex="-1"
                autoComplete="off"
              />
            </Box>
            <TextField
              id="feedback-message"
              label="Feedback message"
              value={message}
              onChange={(event) => {
                recordInput();
                setMessage(event.target.value);
              }}
              onPaste={recordInput}
              multiline
              minRows={6}
              fullWidth
              required
              inputProps={{ maxLength: MAX_LENGTH, 'aria-describedby': 'feedback-character-count' }}
            />
            <Typography id="feedback-character-count" variant="caption" component="div" sx={{ mt: 0.5 }}>
              {message.length} / {MAX_LENGTH} characters
            </Typography>
            {status.message && <Alert severity={status.severity} sx={{ mt: 2 }} role="status">{status.message}</Alert>}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button component={RouterLink} to={ROUTES.root} disabled={submitting}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send feedback'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
}

